import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { format, addDays, compareAsc } from "date-fns";
import { 
  useTheme, 
  useMediaQuery, 
  Box, 
  Typography,   
  CircularProgress, 
  IconButton, 
  Tooltip,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import { fetchJumpSeaters, fetchLegMembersByFlt } from 'state/api';
import { hasPermission } from 'state/checkPermission';
import ScrollToTop from 'components/ScrollToTop';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import FilterButton from 'components/FilterButton';
import DataGridComponent from 'components/DataGridComponent';
import FlightFilterDialog from 'components/FlightFilterDialog';
import CrewListDialog from 'components/CrewListDialog';
import JumpSeaterList from "./JumpSeaterList";


const JumpSeaters = () => {
  useAuthCheck();
  const timeMode = useSelector((state) => state.timemode); 
  const user = useSelector((state) => state.user);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const canView = hasPermission(user?.permissions, "JumpSeaters", "view", false);

  const { startDate, endDate } = useDateRangeContext();

  const scrollRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);  

  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [filters, setFilters] = useState(null);
  const [openCrewListDialog, setOpenCrewListDialog] = useState(false);

  const [flight, setFlight] = useState([]);
  const [crewList, setCrewList] = useState([]);
  const [jumpList, setJumpList] = useState([]);
  const [jumpSeaters, setJumpSeaters] = useState([]);
  
  
  useEffect(() => {    
    const handleJumpSeaters = async () => {
      if (!startDate || !endDate) return [];
      
      try {
        setLoading(true);
        const result = await fetchJumpSeaters(addDays(startDate, -1), addDays(endDate, 1));
        
        if (result) {        
          const filteredSeaters = result.filter((seater) => {            
            const flightDateToCompare = timeMode === 'UTC' 
              ? seater.flightDate 
              : seater.flightDateLocalBase;
          
            return (
              compareAsc(flightDateToCompare, startDate) >= 0 &&
              compareAsc(flightDateToCompare, endDate) <= 0 && 
              seater.seaterType !== 'crew'
            );
          });
          
          setJumpSeaters(filteredSeaters);  
        }
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      } 
    };

    handleJumpSeaters();      
  }, [timeMode, startDate, endDate]); 
  
  const filteredJumpSeaters = useMemo(() => {
    if (!Array.isArray(jumpSeaters)) {
      return []; 
    }

    if (!filters) return jumpSeaters; 

    return jumpSeaters.filter((flight) => {
      const flightEtdStr = timeMode === 'UTC' ? flight.etd?.substring(0, 5) : flight.etdLocalBase?.substring(0, 5);
      const minEtd = filters.minEtd;
      const maxEtd = filters.maxEtd;

      if (minEtd && (!flightEtdStr || flightEtdStr < minEtd)) return false;
      if (maxEtd && (!flightEtdStr || flightEtdStr > maxEtd)) return false;

      return Object.entries(filters).every(([key, value]) => {
        if (!value) return true; // If no value, don't filter on this key
        
        if (key === 'minEtd' || key === 'maxEtd') return true;
        
        if (key === 'flightDate') {
          const formattedFlightDate = flight[key] ? format(new Date(flight[key]), 'yyyy-MM-dd') : null;
          const formattedFilterDate = value ? format(new Date(value), 'yyyy-MM-dd') : null;
          return formattedFlightDate === formattedFilterDate;
        }
            
        if (key === 'flightNo') {
          return flight[key] === Number(value); 
        }
        
        if (key === 'sector') {
          return flight[key]?.toLowerCase().includes(value.toLowerCase());
        }

        if (key === 'acType' || key === 'flightReg') {
          return Array.isArray(value) ? value.includes(flight[key]) : flight[key] === value;
        }
        
        // For other fields, continue normal comparison
        return flight[key] === value;
      });
    });
  }, [timeMode, jumpSeaters, filters]);      

  const handleFilterOpen = () => setIsFilterDialogOpen(true);
  const handleFilterClose = () => setIsFilterDialogOpen(false);

  const handleFilterApply = (filterValues) => {
    setFilters(filterValues); 
  };

  const handleViewClick = useCallback(async (param) => {
    try {      
      const flightDate = param.flightDate;
      const flightDesc = param.flightDesc;
      const dep = param.dep;
      
      const { flightDetails, 
        legMembers, 
        jumpSeaters
      } = await fetchLegMembersByFlt(flightDate, flightDesc, dep);
      
      setFlight(flightDetails);
      setCrewList(legMembers);
      setJumpList(jumpSeaters);
      setOpenCrewListDialog(true);
    } catch (error) {
      setOpenCrewListDialog(false);
    } 
  }, []);

  const handleCloseCrewListDialog = () => {
    setOpenCrewListDialog(false);
    setCrewList([]);
  };

  const columns = useMemo(() => [
    {
      field: "no",
      headerName: "No",
      headerClassName: 'theme-column--header',
      filterable: false,      
      sortable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
      ...(() => {
        return isMobile ? { width: 60 } : { flex: 1 };
      })(),
    },    
    {
      field: timeMode === 'UTC' ? "flightDate" : "flightDateLocalBase",
      headerName: "Date",
      headerClassName: 'theme-column--header',
      valueFormatter: (value) => {
        if (value == null) {
          return '';
        }
        return `${format(value, "dd/MM/yy")}`;
      },
      ...(() => {
        return isMobile ? { width: 90 } : { flex: 1.2 };
      })(),
    },          
    // {
    //   field: "flightDesc",
    //   headerName: "Flt",
    //   headerClassName: 'theme-column--header',
    //   cellClassName: "name-column--cell",
    // },
    // {
    //   field: "flightCarrier",
    //   headerName: "Carrier",
    // },
    {
      field: "flightNo",
      headerName: "FlightNo",
      pinned: 'left',
      headerClassName: 'theme-column--header',
      cellClassName: "name-column--cell",
      ...(() => {
        return isMobile ? { width: 80 } : { flex: 1 };
      })(),
    },    
    {
      field: "sector",
      headerName: "Sector",
      headerClassName: 'theme-column--header',
      ...(() => {
        return isMobile ? { width: 100 } : { flex: 1.5 };
      })(),
    },
    {
      field: "acType",
      headerName: "ACType",
      headerClassName: 'theme-column--header',
      ...(() => {
        return isMobile ? { width: 80 } : { flex: 1 };
      })(),
    },
    {
      field: "flightReg",
      headerName: "Reg",
      headerClassName: 'theme-column--header',
      ...(() => {
        return isMobile ? { width: 90 } : { flex: 1.2 };
      })(),
    },    
    {
      field: timeMode === 'UTC' ? "etd" : "etdLocalBase",
      headerName: "Etd",
      ...(() => {
        return isMobile ? { width: 80 } : { flex: 1 };
      })(),
    },
    {
      field: timeMode === 'UTC' ? "eta" : "etaLocalBase",
      headerName: "Eta",
      ...(() => {
        return isMobile ? { width: 80 } : { flex: 1 };
      })(),
    },    
    {
      field: timeMode === 'UTC' ? "atd" : "atdLocalBase",
      headerName: "Atd",
      headerClassName: 'theme-column--header',
      ...(() => {
        return isMobile ? { width: 80 } : { flex: 1 };
      })(),
    },
    {
      field: timeMode === 'UTC' ? "ata" : "ataLocalBase",
      headerName: "Ata",
      headerClassName: 'theme-column--header',
      ...(() => {
        return isMobile ? { width: 80 } : { flex: 1 };
      })(),
    },
    // { 
    //   field: "staffCode", 
    //   headerName: "Code", 
    // },
    {
      field: "staffName",
      headerName: "StaffName",
      cellClassName: "name-column--cell",
      ...(() => {
        return isMobile ? { width: 300 } : { flex: 3 };
      })(),
    },
    {
      field: "contactTel",
      headerName: "Phone",
      cellClassName: "name-column--cell",
      ...(() => {
        return isMobile ? { width: 100 } : { flex: 1.5 };
      })(),
    },
  ], [timeMode, isMobile]);

  const hiddenColumns = useMemo(() => {  
    return {
      _id: false,
      no: false,
      acType: false,
      flightReg: false,
      atd: false,
      ata: false,
      atdLocalBase: false,
      ataLocalBase: false,
      contactTel: false,
    }
  }, []);

  const nonMobileHiddenColumns = useMemo(() => {
    return {
      _id: false,   
      contactTel: false,
    }
  }, []);

  const actionColumn = useMemo(() => [{
    field: "action",
    headerName: "Action",
    filterable: false,      
    sortable: false,
    width: 80,
    renderCell: (params) => (
      <Box 
        height="100%" 
        display="flex" 
        justifyContent="center" 
        alignItems="center"
        gap={1}
      >
        <Tooltip title="View">
          <IconButton
            aria-label="View"
            sx={{
              color: theme.palette.primary.dark,
              cursor: 'pointer',
              '&:hover': {
                color: theme.palette.green.deep,
              }
            }}
            onClick={() => {
              if (!canView) return;              
              handleViewClick(params.row);
            }}
          >
            <VisibilityIcon />
          </IconButton>
        </Tooltip>          
      </Box>
    ),    
  }], [canView, theme, handleViewClick]); 
    
  const {
    acTypeOptions,
    flightRegOptions,
    sectorTypeOptions,
    departureTypeOptions,
    arrivalTypeOptions,
  } = useMemo(() => {
    if (!Array.isArray(jumpSeaters)) {
      return {
        acTypeOptions: [],
        flightRegOptions: [],
        sectorTypeOptions: [],
        departureTypeOptions: [],
        arrivalTypeOptions: [],
      };
    }

    const sorted = (key) =>
      [...new Set(jumpSeaters.map((f) => f[key]).filter(Boolean))].sort();

    return {
      acTypeOptions: sorted("acType"),
      flightRegOptions: sorted("flightReg"),
      sectorTypeOptions: sorted("sectorType"),
      departureTypeOptions: sorted("departureType"),
      arrivalTypeOptions: sorted("arrivalType"),
    };
  }, [jumpSeaters]); 
  
  const columnsToShow = [...columns, ...actionColumn];
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(
    isMobile ? hiddenColumns : nonMobileHiddenColumns
  );

  useEffect(() => {
    setColumnVisibilityModel(isMobile ? hiddenColumns : nonMobileHiddenColumns);
  }, [isMobile, hiddenColumns, nonMobileHiddenColumns]);

  
  return (
    <Box className="main-container-box">        
      <FlexBetween>
        <Header 
          title="Jump Seaters" 
          subtitle={loading ? "Loading..." : `List of ${filteredJumpSeaters?.length || 0} Seaters`}
        />
        {!loading && (
          <Box display="flex" alignItems="center" gap={1}>  
            <FilterButton
              filters={filters}
              onClick={handleFilterOpen}
            />
          </Box>
        )}                
      </FlexBetween>                
      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <CircularProgress />
        </FlexCenter>
      ) : error ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <Typography color="error">Error: {error?.message || ''}</Typography>
        </FlexCenter>
      ) : (
        <Box
          display="grid"
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
          gap={2}
        >
          <Box
            ref={scrollRef}
            height={LAYOUT_HEIGHT}  
            sx={{ gridColumn: "span 12", overflowY: 'auto' }}    
          >                            
            {isMobile ? (
              <JumpSeaterList 
                jumpSeaters={filteredJumpSeaters}                                
                onClick={handleViewClick}
              />
            ) : (
              <DataGridComponent
                rows={filteredJumpSeaters}
                columns={columnsToShow}
                columnVisibilityModel={columnVisibilityModel}
                setColumnVisibilityModel={setColumnVisibilityModel}
                loading={loading}
              />
            )}

            <ScrollToTop scrollRef={scrollRef} />
          </Box>    
          
          {isFilterDialogOpen &&
            <FlightFilterDialog
              open={isFilterDialogOpen}
              onClose={handleFilterClose}
              onApply={handleFilterApply}
              acTypeOptions={acTypeOptions} 
              flightRegOptions={flightRegOptions}
              sectorTypeOptions={sectorTypeOptions}
              departureTypeOptions={departureTypeOptions}
              arrivalTypeOptions={arrivalTypeOptions}
              currentFilters={filters}
            />
          }
          
          {openCrewListDialog &&
            <CrewListDialog 
              open={openCrewListDialog}
              onClose={handleCloseCrewListDialog}
              flight={flight}
              crewList={crewList}
              jumpList={jumpList}
            />
          }
        </Box>
      )}
    </Box>
  );
};

export default JumpSeaters;
